export interface ShareLink {
  id: string
  label: string
  created_at: string
  expires_at: string | null
  revoked_at: string | null
  created_by_name: string
  live: boolean
}

export interface ShareLinkMinted extends ShareLink {
  token: string
}

export type ShareLinkStatus = 'live' | 'revoked' | 'expired'

const SHARE_PATH_PREFIX = '/share/'

export const SHARE_EXPIRY_CHOICES = [1, 7, 30, 90, 365] as const

/** What a warning calls a link; the label is optional, so an unlabelled one goes by who made it and when. */
export function shareLinkName(link: ShareLink, formatDate: (iso: string) => string): string {
  return link.label || `${link.created_by_name} · ${formatDate(link.created_at)}`
}

/** Mirrors the server's `is_public_only`: the public view and no ordinary account. */
export function isPublicOnly(user: { is_admin: boolean; abilities: string[] }): boolean {
  return !user.is_admin && user.abilities.includes('public.view') && !user.abilities.includes('basic')
}

export function shareUrl(origin: string, token: string): string {
  return `${origin.replace(/\/+$/, '')}${SHARE_PATH_PREFIX}${encodeURIComponent(token)}`
}

/** The token in a `/share/<token>` path, or null for any other path. */
export function shareTokenFromPath(pathname: string): string | null {
  if (!pathname.startsWith(SHARE_PATH_PREFIX)) return null
  const raw = pathname.slice(SHARE_PATH_PREFIX.length).replace(/\/+$/, '')
  if (!raw || raw.includes('/')) return null
  try {
    return decodeURIComponent(raw)
  } catch {
    return null
  }
}

/** Revoked outranks expired; an unparseable expiry reads as expired, never live. */
export function shareLinkStatus(link: Pick<ShareLink, 'revoked_at' | 'expires_at'>, now: Date): ShareLinkStatus {
  if (link.revoked_at) return 'revoked'
  if (link.expires_at === null) return 'live'
  const expires = Date.parse(link.expires_at)
  if (Number.isNaN(expires) || expires <= now.getTime()) return 'expired'
  return 'live'
}
